import type Handsontable from 'handsontable';

/**
 * 行拖拽状态机
 *
 * 由 useRowDragDelegation 在首列拖拽 icon 的 mousedown 时触发。
 *
 * 交互规则：
 * - 鼠标位移 < DRAG_THRESHOLD：视为点击 → 调用 onClickSelect(row) + hot.selectRows(row)
 * - 鼠标位移 >= DRAG_THRESHOLD：进入拖拽 → 显示 guideline，mouseup 时调用 ManualRowMove.dragRow(from, dropIndex)
 *   （dropIndex 为视觉放置位置，HOT 内部 countFinalIndex 自动修正向下移动的索引偏移）
 * - 首行（row===0）为语义表头：不可作为源、不可作为落点上方
 *
 * 拆出的好处：
 * - 内部状态机（dragStarted / guidelineEl / dropInfo）封装在闭包内，不污染组件
 * - 可独立测试（mock hot.getCell / getCoords / dragRow，断言 guideline 创建/销毁）
 */

/** 视为「拖拽开始」所需的最小鼠标位移（px） */
export const DRAG_THRESHOLD = 4;

export const beginRowDrag = (
  hot: Handsontable,
  fromRow: number,
  startEvent: MouseEvent,
  onClickSelect: (rowIdx: number) => void,
): void => {
  if (fromRow <= 0) {
    onClickSelect(fromRow);
    hot.selectRows(fromRow);
    return;
  }

  const startY = startEvent.clientY;
  const startX = startEvent.clientX;
  const tableRoot = hot.rootElement as HTMLElement;
  let dragStarted = false;
  let guidelineEl: HTMLDivElement | null = null;
  let dropInfo = { targetRow: fromRow, insertBefore: true };

  const ensureGuideline = (): HTMLDivElement => {
    if (guidelineEl) return guidelineEl;
    const el = document.createElement('div');
    el.className = 'dba-smart-table-row-drag-guideline';
    tableRoot.appendChild(el);
    guidelineEl = el;
    return el;
  };

  const getTrByRow = (rowIdx: number): HTMLElement | null => {
    const cell = hot.getCell(rowIdx, 0);
    return cell ? (cell as HTMLElement).closest('tr') : null;
  };

  const updateGuideline = (targetRow: number, insertBefore: boolean) => {
    const tr = getTrByRow(targetRow);
    if (!tr) return;
    const el = ensureGuideline();
    const rootRect = tableRoot.getBoundingClientRect();
    const trRect = tr.getBoundingClientRect();
    el.style.top = `${(insertBefore ? trRect.top : trRect.bottom) - rootRect.top}px`;
    el.style.left = '0px';
    el.style.width = `${tableRoot.clientWidth}px`;
  };

  const resolveHoverRow = (clientX: number, clientY: number): number | null => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const td = el?.closest('tr')?.querySelector('td') as HTMLElement | null;
    if (!td) return null;
    const coords = hot.getCoords(td);
    return coords ? coords.row : null;
  };

  const onMove = (e: MouseEvent) => {
    if (!dragStarted) {
      if (Math.abs(e.clientY - startY) < DRAG_THRESHOLD && Math.abs(e.clientX - startX) < DRAG_THRESHOLD) return;
      dragStarted = true;
      document.body.classList.add('dba-smart-table-row-dragging');
    }

    const hoverRow = resolveHoverRow(e.clientX, e.clientY);
    if (hoverRow === null) return;

    if (hoverRow <= 0) {
      dropInfo = { targetRow: 1, insertBefore: true };
      updateGuideline(1, true);
      return;
    }

    const tr = getTrByRow(hoverRow);
    if (!tr) return;
    const trRect = tr.getBoundingClientRect();
    const insertBefore = e.clientY < trRect.top + trRect.height / 2;
    dropInfo = { targetRow: hoverRow, insertBefore };
    updateGuideline(hoverRow, insertBefore);
  };

  const onUp = () => {
    if (!dragStarted) {
      onClickSelect(fromRow);
      hot.selectRows(fromRow);
      cleanup();
      return;
    }

    const { targetRow, insertBefore } = dropInfo;
    cleanup();

    let dropIndex = insertBefore ? targetRow : targetRow + 1;
    if (dropIndex < 1) dropIndex = 1;
    const totalRows = hot.countRows();
    if (dropIndex > totalRows) dropIndex = totalRows;
    if (dropIndex === fromRow || dropIndex === fromRow + 1) return;

    const plugin = hot.getPlugin('manualRowMove') as unknown as {
      dragRow?: (from: number, dropIndex: number) => boolean;
    } | undefined;
    if (plugin?.dragRow?.(fromRow, dropIndex)) hot.render();
  };

  const cleanup = () => {
    document.removeEventListener('mousemove', onMove, true);
    document.removeEventListener('mouseup', onUp, true);
    guidelineEl?.parentElement?.removeChild(guidelineEl);
    guidelineEl = null;
    document.body.classList.remove('dba-smart-table-row-dragging');
  };

  document.addEventListener('mousemove', onMove, true);
  document.addEventListener('mouseup', onUp, true);
};
